import React from "react"

import { Alert } from "../alert/Alert"
import { ConfirmationFrame } from "../layout/frames/ConfirmationFrame"
import { FlexLayout } from "../layout/flex/FlexLayout"
import { ScrollableLayout } from "../layout/scrollable/ScrollableLayout"
import { HotKeyCode } from "../keyboard/HotKeyCode"
import { LocalizedString } from "../i18n/Translator"
import { LocalizedText } from "../text/LocalizedText"
import { PlaySound } from "../sound/PlaySound"

type ChromeVoiceUnsupportedLocaleModalStringKey =
  "caption" | "suggestedLanguages" | "signOut"

export type ChromeVoiceUnsupportedLocaleModalStrings =
  Partial<Record<ChromeVoiceUnsupportedLocaleModalStringKey, string>>

export interface VoiceSupportedLocaleMappedToRegion {
  language: string
  regions: string[]
}

interface ChromeVoiceUnsupportedLocaleModalProps {
  /**
   * Language associate selected in FCMenu, shown in the banner (e.g.
   * "Tagalog")
   */
  language: string

  /**
   * The languages voice does support, listed so the associate knows what to
   * switch to in FCMenu.
   */
  supportedLanguages: VoiceSupportedLocaleMappedToRegion[]

  /**
   * Callback executed when the associate chooses to sign out.
   */
  onSignOut?: () => void
}

/**
 * Blocking modal shown when the associate's FCMenu locale is not supported by
 * the voice system.
 *
 * The only action supported on modal is to sign out, change the language in FCMenu
 * and sign back in (or switch to non-voice Pick).
 */
export class ChromeVoiceUnsupportedLocaleModal
  extends React.PureComponent<ChromeVoiceUnsupportedLocaleModalProps> {
  public static defaultStrings: ChromeVoiceUnsupportedLocaleModalStrings = {}

  public static readonly defaultTexts:
    Record<ChromeVoiceUnsupportedLocaleModalStringKey, string> = {
    // {language} is the unsupported language the associate selected.
    caption: "[{language}] not supported. Change language in FC Menu.",
    suggestedLanguages: "Suggested languages:",
    signOut: "Sign out",
  }

  public static readonly hotKeys: Record<"signOut", HotKeyCode> = {
    signOut: "Enter",
  }

  private renderLanguage({ language, regions }: VoiceSupportedLocaleMappedToRegion) {
    const label = regions.length > 1
      ? `${language} (${regions.join(", ")})`
      : language

    return (
      <li key={language}>
        <LocalizedText variant="black" defaultString={label} />
      </li>
    )
  }

  public render() {
    const { defaultTexts, defaultStrings, hotKeys } = ChromeVoiceUnsupportedLocaleModal
    const { language, supportedLanguages } = this.props

    const caption: LocalizedString = {
      stringID: defaultStrings.caption,
      defaultString: defaultTexts.caption,
      parameters: { language },
    }

    return (
      <ConfirmationFrame
        primaryButtonLabel={{
          stringID: defaultStrings.signOut,
          defaultString: defaultTexts.signOut,
        }}
        primaryButtonHotKey={hotKeys.signOut}
        onPrimaryButtonClick={this.props.onSignOut}
      >
        <PlaySound id="voiceUnsupportedLocale" sound="attention">
          <FlexLayout direction="column">
            <FlexLayout.Item padding="sm">
              <FlexLayout
                direction="column"
                padding="sm"
                backgroundColor="white"
                rounded={true}
              >
                <FlexLayout.Item size="auto" paddingBottom="sm">
                  <Alert
                    variant="warning"
                    inactive={true}
                    rounded={true}
                    title={caption}
                  />
                </FlexLayout.Item>

                <FlexLayout.Item size="auto">
                  <LocalizedText
                    variant="black"
                    stringID={defaultStrings.suggestedLanguages}
                    defaultString={defaultTexts.suggestedLanguages}
                  />
                </FlexLayout.Item>

                <FlexLayout.Item>
                  <ScrollableLayout>
                    <ul>
                      {supportedLanguages.map((option) => this.renderLanguage(option))}
                    </ul>
                  </ScrollableLayout>
                </FlexLayout.Item>
              </FlexLayout>
            </FlexLayout.Item>
          </FlexLayout>
        </PlaySound>
      </ConfirmationFrame>
    )
  }
}
